import React, { useEffect, useState } from "react";
import { Row } from "react-bootstrap";
import "react-bootstrap-multiselect/css/bootstrap-multiselect.css";
import BootstrapTable from "react-bootstrap-table-next";
import "react-dropdown-tree-select/dist/styles.css";
import { toast } from "react-toastify";
import LoggedInLayout from "../components/LoggedInLayout";
import PageLoader from "../components/PageLoader";
import PaginationTable from "../components/PaginationTable";
import PeriodDropdown from "../components/PeriodDropdown";
import { Constants } from "../libs/Constants";
import {
  calculateEquivalentShares,
  getPurchasePeriodsAsArray,
} from "../libs/contributionLib";
import { getList } from "../libs/dbFunctions";
import {
  convertToDollar,
  formatCurrency,
  numberWithCommas,
} from "../libs/utils";
import "./ExpenseDashboard.scss";

const periodOptions = [
  {
    key: Constants.PURCHASE_PERIOD_DISPLAY_TYPES.PURCHASE,
    value: Constants.PURCHASE_PERIOD_DISPLAY_TYPES.PURCHASE,
    children: [],
  },
];
const columns = [
  {
    dataField: "empid",
    text: "Employee ID",
    sort: true,
    classes: "p-1",
  },
  {
    dataField: "firstName",
    sort: true,
    headerStyle: () => {
      return { width: "150px" };
    },
    text: "First Name",
    classes: "p-1",
    hidden: false,
  },
  {
    dataField: "lastName",
    sort: true,
    headerStyle: () => {
      return { width: "150px" };
    },
    text: "Last Name",
    classes: "p-1",
    hidden: false,
  },
  {
    dataField: "costCenter",
    sort: true,
    text: "Cost Center",
    headerStyle: () => {
      return { width: "200px" };
    },
    classes: "p-1",
    hidden: false,
  },
  {
    dataField: "subsidiary",
    sort: true,
    text: "Subsidiary",
    classes: "p-1",
    headerStyle: () => {
      return { width: "200px" };
    },
    hidden: false,
  },
  {
    dataField: "contributionValue",
    sort: true,
    text: "Contribution amount",
    classes: "p-1",
    formatter: (cell) => {
      return formatCurrency(convertToDollar(cell));
    },
  },
  {
    dataField: "shares",
    sort: true,
    text: "Shares",
    classes: "p-1",
    formatter: (cell) => {
      return numberWithCommas(cell);
    },
  },
  {
    dataField: "reason",
    sort: true,
    text: "Reason",
  },
];

const ExceptionReport = (props) => {
  const [periods, setPeriods] = useState([]);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [contributions, setContributions] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [currentPageSize, setCurrentPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(0);
  const [lastKey, setLastKey] = useState(null);
  const [lastFilterOptions, setLastFilterOptions] = useState({});

  useEffect(() => {
    async function load() {
      props.setPageLoading(true);
      let promises = [getPlan(), loadPeriods()];
      await Promise.all(promises);
      props.setPageLoading(false);
    }
    load();
  }, []);
  useEffect(() => {
    let totalLoaded = contributions.length;
    let totalPages = totalLoaded / currentPageSize;
    let needMoreRecords = totalPages - currentPage < 4 && !!lastKey;
    if (needMoreRecords) {
      loadContribution(null, true);
    }
  }, [currentPage, currentPageSize]);
  useEffect(() => {
    filterForException();
  }, [contributions]);

  function filterForException() {
    setLoading(true);
    if (contributions && contributions.length > 0) {
      let filteredContributions = [];
      for (let contribution of contributions) {
        let purchasePeriods = getPurchasePeriodsAsArray(contribution, false);
        let purchasePeriodsLength = purchasePeriods.length,
          lastModificationOfUser =
            purchasePeriods[purchasePeriodsLength - 1][
              purchasePeriods[purchasePeriodsLength - 1].length - 1
            ];
        if (
          lastModificationOfUser.type ===
            Constants.CONTRIBUTION_STATUS.WITHDRAW &&
          lastModificationOfUser.type ===
            Constants.CONTRIBUTION_STATUS.TERMINATE
        ) {
          continue;
        }
        let totalShares = 0;
        let contributionAmount = 0;
        for (let purchasePeriod of purchasePeriods) {
          for (let c of purchasePeriod) {
            let shares = calculateEquivalentShares(
              c.contributionValue,
              contribution?.purchasePrice
            );
            switch (c.type) {
              case Constants.CONTRIBUTION_STATUS.NEW:
              case Constants.CONTRIBUTION_STATUS.INCREASE:
              case Constants.CONTRIBUTION_STATUS.RESET_NEW:
                totalShares += shares;
                contributionAmount += c.contributionValue;
                break;
              case Constants.CONTRIBUTION_STATUS.DECREASE:
                totalShares -= shares;
                contributionAmount += c.contributionValue;
                break;
            }
          }
        }
        Object.assign(contribution, {
          shares: totalShares,
          contributionValue: contributionAmount,
        });
        filteredContributions.push(contribution);
      }
      let processed = [];
      for (let c of filteredContributions) {
        if (
          plan?.minContribution?.stock &&
          c.shares < plan?.minContribution?.stock
        ) {
          c.reason = "Min stock not reached";
          processed.push(c);
        }
        if (
          plan?.maxContribution?.stock &&
          c.shares > plan?.maxContribution?.stock
        ) {
          c.reason = "Max stock reached";
          processed.push(c);
        }
      }
      setTableData(processed);
    } else setTableData([]);
    setLoading(false);
  }

  async function loadPeriods() {
    try {
      const periods = await getList(
        Constants.dbTypes.PERIOD,
        props.activeCompany
      );
      if (periods && periods.length > 0) {
        setPeriods(periods);
      }
    } catch (e) {
      toast.error(e);
    }
  }

  async function getPlan() {
    try {
      const planDetail = await getList(
        Constants.dbTypes.PLAN_DETAILS,
        props.activeCompany
      );
      if (planDetail && planDetail.length > 0) {
        setPlan(planDetail[0]);
      }
    } catch (e) {
      toast.error(e);
    }
  }

  async function loadContribution(filter, reloadContributions = false) {
    let sendLastKey = lastKey ? lastKey : null;
    if (reloadContributions) {
      filter = lastFilterOptions;
    } else {
      sendLastKey = null;
      setLastKey(null);
      setLastFilterOptions(filter);
    }
    filter = { ...filter, pagination: true, lastKey: sendLastKey };
    setLoading(true);
    try {
      let contributionsResult = await getList(
        Constants.dbTypes.OFFERING_PERIOD_CONTRIBUTIONS,
        props.activeCompany,
        filter
      );
      if (contributionsResult?.lastKey) {
        setLastKey(contributionsResult.lastKey);
      } else {
        setLastKey(null);
      }
      if (contributionsResult && contributionsResult.length > 0) {
        let result = contributions.concat(contributionsResult);
        setContributions(result);
      } else {
        setContributions([]);
      }
    } catch (e) {
      toast.error(e);
      setContributions([]);
    }

    setLoading(false);
  }

  function handlePeriodSelect(selectedPeriod) {
    if (selectedPeriod && selectedPeriod?.type) {
      switch (selectedPeriod.type) {
        case Constants.PERIOD_DISPLAY_TYPES.CONTRIBUTION:
          loadContribution({
            offerStartdate: selectedPeriod?.period?.periodStartdate,
          });
          break;
        case Constants.PERIOD_DISPLAY_TYPES.PURCHASE:
          loadContribution({
            periodStartdate: selectedPeriod.period.periodStartdate,
            periodEnddate: selectedPeriod.period.periodEnddate,
          });
          break;
      }
    }
  }
  const contentTable = ({ paginationTableProps }) => {
    return (
      <PageLoader isActive={loading} individual={true}>
        <BootstrapTable
          bootstrap4={true}
          wrapperClasses="table-vw-height mb-2"
          classes="table-taabl"
          keyField="id"
          bordered={false}
          data={tableData}
          columns={columns}
          {...paginationTableProps}
        />
      </PageLoader>
    );
  };
  function renderContributionTable() {
    if (tableData && tableData.length > 0) {
      return (
        <PaginationTable
          onPageChange={setCurrentPage}
          onPageSizeChange={setCurrentPageSize}
          totalRow={tableData?.length}
        >
          {contentTable}
        </PaginationTable>
      );
    } else {
      return (
        <div className="me-3 mt-5">
          No Exceptions for this company in the given Period.
        </div>
      );
    }
  }
  return (
    <LoggedInLayout title="Exception Report">
      {periods && plan && (
        <Row className="ms-0">
          <PeriodDropdown
            periods={periods}
            plan={plan}
            options={periodOptions}
            handlePeriodSelect={handlePeriodSelect}
            activeCompany={props.activeCompany}
          ></PeriodDropdown>
        </Row>
      )}
      {renderContributionTable()}
    </LoggedInLayout>
  );
};

export default ExceptionReport;
